import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { FragmentNames, WoundsDressingsListAliases } from '../../../../codegen/index';
import BaseController from '../../../BaseController';
import { ClickEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import './wounds-dressings-list-controller.css';
import { ApplicationRoute } from '../../../../constants/routes';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { WoundsResponse } from '../../types/wounds-dressings-types';
import { Endpoint } from '../../../../constants/endpoints';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants/lib';
import { PanelAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { Inject } from '@odinjs/odin';
import { ItemPepType, RulesItemConfiguration } from '../../../../constants/utils-pep';
import { YesNoType } from '../../../../utils/enum-utils';
import { WoundsDressingContext } from '../../wounds-dressing-service';
import InformationBarUtils from '../../../../controllers/commons/header/information-bar-header-utils';

@Controller({ component: WoundsDressingsListAliases.LAYOUT_WOUNDS_DRESSINGS_LIST, fragment: FragmentNames.WOUNDS_DRESSINGS_LIST })
class WoundsDressingsListController extends BaseController {

  @Inject(WoundsDressingsListAliases.PANEL_ACTIONS_WOUNDS_DRESSINGS)
  private panelGlobalActionsApi!: PanelAPI;

  @OnMount({target: WoundsDressingsListAliases.LAYOUT_WOUNDS_DRESSINGS_LIST})
  handleMountLayout(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const establishmentId = foundationContext.getEstablishment()?.id;
    this.context.set({ encounterId, establishmentId, patientId });

    this.checkItemEditPermission()
      .finally(() => {
        const allowInfo = InformationBarUtils.allowInfo && WoundsDressingContext.getAllowEdit();
        this.panelGlobalActionsApi.setVisible(allowInfo);
      });
  }

  @OnClick(WoundsDressingsListAliases.DECK_WOUNDS_DRESSINGS)
  handleOnClick(event: ClickEvent): void {
    const woundId = event.value.id;
    this.handleFetchWounds(woundId, event);
  }

  private checkItemEditPermission(): Promise<void> {
    const itemType = ItemPepType.WOUNDS_DRESSINGS;
    return HttpService.get(Endpoint.PEP_CAN_EDIT_ITEM, { itemType })
      .then(response => {
        const configuration = response.body as RulesItemConfiguration;
        const isAllowEdit = configuration?.allowEditing === YesNoType.YES;
        WoundsDressingContext.setAllowEdit(isAllowEdit);
        Promise.resolve(response);
      });
  }

  private handleFetchWounds(woundId: number, event: ClickEvent): Promise<WoundsResponse | void> {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const textUninformed = MessageService.translate('text.uninformed');
    const allowInfo = InformationBarUtils.allowInfo && WoundsDressingContext.getAllowEdit();
    const accessViewer = event.value.releaseDate || !allowInfo;
    const route = accessViewer ? ApplicationRoute.WOUNDS_DRESSINGS_TAB_LIST : ApplicationRoute.WOUNDS_EDIT;

    return HttpService.get(Endpoint.WOUND, {
      sequenceNumberWound: woundId
    })
      .then(response => {
        const responseBody = response.body as WoundsResponse;
        const classification = responseBody.classification?.description != undefined ? responseBody.classification?.description.concat(',') : '';
        const woundShape = responseBody.woundShape?.description ?? '';
        const location = responseBody.location?.description ?? '';
        const woundTypeDescription = responseBody.woundType?.description ?? '';
        const woundType = classification === '' && woundTypeDescription != '' ? woundTypeDescription.concat(',') : woundTypeDescription;
        const woundLocationDescription = `${woundType} ${classification} ${woundShape} ${location}`;
        const woundLocation = woundLocationDescription.trim() === '' ? textUninformed : woundLocationDescription;
        applicationContext.redirect(route, {
          encounterId,
          patientId,
          woundId,
          woundLocation
        });
        Promise.resolve(responseBody);
      }).catch((error) => this.showToast(error?.body.errorMessage, ToastLevel.ERROR));
  }

  @OnClick(WoundsDressingsListAliases.BUTTON_ADD_WOUNDS_DRESSINGS)
  handleClickAddButton(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.redirect(
      ApplicationRoute.WOUNDS_CREATE, {
        encounterId,
        patientId
      }
    )
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'wounds-dressings-list',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

}

export default WoundsDressingsListController;
